/**
 * BackendOfflineBanner
 *
 * Shown in the AppShell header whenever the backend /health endpoint is
 * unreachable.  Renders nothing while the backend is online or still being
 * checked for the first time (avoids false-positive flash on slow networks).
 *
 * The user can dismiss it until the next offline event or use the Retry
 * button to trigger an immediate re-check.
 */
import { useState } from 'react';
import { WifiOff, RefreshCw, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useBackendStatus } from '@/hooks/useBackendStatus';

export function BackendOfflineBanner() {
  const { status, recheck } = useBackendStatus();
  const [dismissed, setDismissed] = useState(false);

  // Un-dismiss automatically when connectivity is restored so the user
  // sees confirmation that the app is back online briefly — but we only
  // show the banner while offline, so the restored state is silent.
  const wasOffline = status === 'offline';
  if (!wasOffline || dismissed) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-1 text-label',
        'bg-amber-500/10 text-amber-700 dark:text-amber-400',
      )}
      role="status"
      aria-live="polite"
    >
      <WifiOff size={12} strokeWidth={2} />

      <span className="font-medium">Backend unreachable</span>
      <span className="text-fg-tertiary">— working offline</span>

      <button
        type="button"
        onClick={() => { setDismissed(false); recheck(); }}
        title="Retry connection"
        className="ml-1 rounded p-0.5 transition-colors hover:bg-amber-500/20"
      >
        <RefreshCw size={11} strokeWidth={2} />
      </button>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        title="Dismiss"
        className="rounded p-0.5 transition-colors hover:bg-amber-500/20"
      >
        <X size={11} strokeWidth={2} />
      </button>
    </div>
  );
}
